
import { useState } from 'react';
import { useBoardCollaboration } from '@/hooks/useBoardCollaboration';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Mail, Send, X, Clock, AlertCircle } from 'lucide-react';

interface BoardInvitationsTabProps {
  boardId: string;
}

export const BoardInvitationsTab = ({ boardId }: BoardInvitationsTabProps) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');

  const {
    boardInvitations,
    canManageMembers,
    invitationsLoading,
    inviteUser,
    cancelInvitation,
    isInviting,
    isCancellingInvitation
  } = useBoardCollaboration(boardId);

  const handleInvite = () => {
    if (email && role) {
      inviteUser({ email, role });
      setEmail('');
      setRole('viewer');
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isExpired = (expiresAt: string) => {
    return new Date() > new Date(expiresAt);
  };

  return (
    <div className="space-y-6">
      {canManageMembers && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send Invitation
            </CardTitle>
            <CardDescription>
              Invite new members to collaborate on this board. An email will be sent with an invitation link.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                disabled={isInviting}
              />
            </div>

            <div>
              <Label htmlFor="role">Role</Label>
              <Select value={role} onValueChange={setRole} disabled={isInviting}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="viewer">Viewer - Can view board content</SelectItem>
                  <SelectItem value="editor">Editor - Can create and edit cards</SelectItem>
                  <SelectItem value="admin">Admin - Can manage members and settings</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleInvite}
              disabled={!email || !isValidEmail(email) || isInviting}
              className="flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {isInviting ? 'Sending Invitation...' : 'Send Invitation'}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Pending Invitations</CardTitle>
          <CardDescription>
            Manage outstanding invitations that haven't been accepted yet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invitationsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2">Loading invitations...</span>
            </div>
          ) : (
            <div className="space-y-4">
              {boardInvitations?.map((invitation) => {
                const expired = isExpired(invitation.expires_at);
                
                return (
                  <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${expired ? 'bg-red-100 dark:bg-red-900' : 'bg-blue-100 dark:bg-blue-900'}`}>
                        {expired ? (
                          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                        ) : (
                          <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                      <div>
                        <div className="font-medium">{invitation.email}</div>
                        <div className="text-sm text-gray-500 flex items-center gap-2">
                          <Clock className="h-3 w-3" />
                          Invited {new Date(invitation.invited_at).toLocaleDateString()}
                          {expired ? (
                            <span className="text-red-500 font-medium">• Expired</span>
                          ) : (
                            <>• Expires {new Date(invitation.expires_at).toLocaleDateString()}</>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Badge variant={expired ? "destructive" : "outline"}>
                        {invitation.role}
                      </Badge>

                      {canManageMembers && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={isCancellingInvitation}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to cancel the invitation for {invitation.email}?
                                They will no longer be able to join this board using the current invitation link.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Keep Invitation</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => cancelInvitation(invitation.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Cancel Invitation
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </div>
                );
              })}

              {boardInvitations?.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No pending invitations.
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
